// International Settings — ShipStation-parity customs defaults + tax identifiers.
//
// Default customs information applied to international orders, plus the registered
// tax identifiers (VAT / IOSS / EORI) and the prepaid duties toggle. V1 operational
// simulation (MSW); prod serves international_settings + international_tax_identifiers.

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { API_BASE } from '../../../lib/api'
import { Card } from '../../../components/ui/card'
import { DataTableShell } from '../../../components/ui/data-table-shell'
import { useT } from '../../../i18n'
import { internationalSettingsI18n } from '../../../i18n/admin/international-settings'

type Settings = {
  default_content_type: string
  non_delivery_option: string
  customs_declarations: string
  sign_customs_as: string | null
  itn_exemption: string | null
  prepaid_duties: boolean
}
type TaxId = {
  id: string
  tax_id_type: string
  issuing_authority: string
  number: string
  nickname: string | null
  description: string | null
}

const base = `${API_BASE}/admin/shipping/international`

// Option values sent to the server; labels come from internationalSettingsI18n.
const CONTENT_TYPES = ['merchandise', 'documents', 'gift', 'returned_goods', 'sample', 'other'] as const
const NON_DELIVERY = ['return_to_sender', 'treat_as_abandoned'] as const
const DECLARATIONS = ['from_order_items', 'manual'] as const

function Select({
  label,
  value,
  onChange,
  options,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: readonly (readonly [string, string])[]
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-slate-600">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </label>
  )
}

export function InternationalSettings() {
  const t = useT(internationalSettingsI18n)
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['international-settings'],
    queryFn: async (): Promise<Settings> => {
      const res = await fetch(base)
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
  })
  const [form, setForm] = useState<Settings | null>(null)
  // Seed the editable form once from the loaded settings (adjust-during-render:
  // the condition turns itself off after the first set).
  if (data && !form) setForm(data)

  const save = useMutation({
    mutationFn: async () => {
      const res = await fetch(base, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(form),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['international-settings'] }),
  })

  const set = <K extends keyof Settings>(k: K, v: Settings[K]) =>
    setForm((f) => (f ? { ...f, [k]: v } : f))

  if (!form) return <Card className="p-8 text-center text-sm text-slate-400">{t.loading}</Card>

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">{t.title}</h1>
        <p className="mt-0.5 text-sm text-slate-500">
          {t.description}
        </p>
      </div>

      <Card className="space-y-4 p-5">
        <h2 className="text-sm font-semibold text-slate-700">{t.customsTitle}</h2>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select label={t.defaultContentType} value={form.default_content_type} onChange={(v) => set('default_content_type', v)} options={CONTENT_TYPES.map((v) => [v, t.contentTypes[v]] as const)} />
          <Select label={t.nonDeliveryOption} value={form.non_delivery_option} onChange={(v) => set('non_delivery_option', v)} options={NON_DELIVERY.map((v) => [v, t.nonDelivery[v]] as const)} />
          <Select label={t.customsDeclarations} value={form.customs_declarations} onChange={(v) => set('customs_declarations', v)} options={DECLARATIONS.map((v) => [v, t.declarations[v]] as const)} />
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-600">{t.signCustomsAs}</span>
            <input
              value={form.sign_customs_as ?? ''}
              onChange={(e) => set('sign_customs_as', e.target.value || null)}
              className="w-full rounded-md border border-slate-300 px-2.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400"
            />
          </label>
        </div>
      </Card>

      <TaxIdentifiers />

      <Card className="space-y-2 p-5">
        <h2 className="text-sm font-semibold text-slate-700">{t.dutiesTitle}</h2>
        <label className="flex items-start gap-2.5 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={form.prepaid_duties}
            onChange={(e) => set('prepaid_duties', e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-600 focus:ring-brand-400"
          />
          <span>
            {t.prepaidDuties}
            <span className="mt-0.5 block text-xs text-slate-500">
              {t.prepaidDutiesHint}
            </span>
          </span>
        </label>
      </Card>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={save.isPending}
          className="rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
        >
          {save.isPending ? t.saving : t.saveChanges}
        </button>
      </div>
    </div>
  )
}

function TaxIdentifiers() {
  const t = useT(internationalSettingsI18n)
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: ['tax-identifiers'],
    queryFn: async (): Promise<{ items: TaxId[] }> => {
      const res = await fetch(`${base}/tax-identifiers`)
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
  })
  const invalidate = () => qc.invalidateQueries({ queryKey: ['tax-identifiers'] })
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState({ tax_id_type: 'VAT', issuing_authority: '', number: '', nickname: '' })

  const add = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${base}/tax-identifiers`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(draft),
      })
      if (!res.ok) throw new Error(`${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      setAdding(false)
      setDraft({ tax_id_type: 'VAT', issuing_authority: '', number: '', nickname: '' })
      invalidate()
    },
  })
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(`${base}/tax-identifiers/${id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error(`${res.status}`)
    },
    onSuccess: invalidate,
  })

  return (
    <Card className="space-y-3 p-5">
      <h2 className="text-sm font-semibold text-slate-700">{t.taxTitle}</h2>
      <DataTableShell variant="embedded">
      <table>
        <thead>
          <tr>
            <th>{t.colType}</th>
            <th>{t.colAuthority}</th>
            <th>{t.colNumber}</th>
            <th>{t.colNickname}</th>
            <th className="text-right" />
          </tr>
        </thead>
        <tbody>
          {(data?.items ?? []).map((tax) => (
            <tr key={tax.id}>
              <td className="font-medium text-[var(--color-text)]">{tax.tax_id_type}</td>
              <td className="text-[var(--color-text-muted)]">{tax.issuing_authority}</td>
              <td className="tabular-nums text-[var(--color-text-muted)]">{tax.number}</td>
              <td className="text-[var(--color-text-muted)]">{tax.nickname}</td>
              <td className="text-right">
                <button
                  type="button"
                  onClick={() => remove.mutate(tax.id)}
                  className="text-xs font-medium text-[var(--color-danger)] hover:underline"
                >
                  {t.delete}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </DataTableShell>

      {adding ? (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-4">
          <select
            value={draft.tax_id_type}
            onChange={(e) => setDraft((d) => ({ ...d, tax_id_type: e.target.value }))}
            className="rounded-md border border-slate-300 px-2 py-1.5 text-sm"
          >
            {['VAT', 'IOSS', 'EORI'].map((v) => (
              <option key={v}>{v}</option>
            ))}
          </select>
          <input placeholder={t.authorityPlaceholder} value={draft.issuing_authority} onChange={(e) => setDraft((d) => ({ ...d, issuing_authority: e.target.value }))} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <input placeholder={t.numberPlaceholder} value={draft.number} onChange={(e) => setDraft((d) => ({ ...d, number: e.target.value }))} className="rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
          <div className="flex gap-1">
            <input placeholder={t.nicknamePlaceholder} value={draft.nickname} onChange={(e) => setDraft((d) => ({ ...d, nickname: e.target.value }))} className="min-w-0 flex-1 rounded-md border border-slate-300 px-2 py-1.5 text-sm" />
            <button type="button" onClick={() => add.mutate()} disabled={!draft.number.trim() || add.isPending} className="rounded-md bg-emerald-600 px-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50">{t.save}</button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 hover:underline"
        >
          <Plus className="h-3.5 w-3.5" /> {t.addAnother}
        </button>
      )}
    </Card>
  )
}
